import "./LandingMenu.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Menu,
  X,
  Landmark,
  MessagesSquare,
  FileQuestionMark,
  LayoutDashboard,
} from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const DASHBOARD_PATHS = {
  HOMEOWNER: "/hb/dashboard",
  LENDER: "/lender/dashboard",
  OSS: "/oss/dashboard",
  PLATFORM_ADMIN: "/admin/dashboard",
};

export default function LandingMenu({
  title,
  items,
  mobileOpen,
  onOpen,
  onClose,
}) {
  const { t } = useTranslation();

  const {
    isAuthenticated,
    isLoading: authLoading,
    getAccessTokenSilently,
    user,
  } = useAuth0();

  const location = useLocation();

  const prefersReducedMotion = useReducedMotion();

  const menuRef = useRef(null);

  const [pinned, setPinned] = useState(false);

  const [internalMobileOpen, setInternalMobileOpen] = useState(false);

  const [dashboardPath, setDashboardPath] = useState("");

  const menuTitle = title || t("menuTitle");

  const isHomePage = location.pathname === "/";

  useEffect(() => {
    let cancelled = false;

    const loadCurrentUser = async () => {
      if (authLoading) {
        return;
      }

      if (!isAuthenticated) {
        if (!cancelled) {
          setDashboardPath("");
        }

        return;
      }

      if (user?.email_verified !== true) {
        if (!cancelled) {
          setDashboardPath("");
        }

        return;
      }

      try {
        const token = await getAccessTokenSilently();

        const response = await fetch(`${API_BASE_URL}/api/me/`, {
          method: "GET",

          headers: {
            Authorization: `Bearer ${token}`,

            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Could not load current user (${response.status}).`);
        }

        const me = await response.json();

        const resolvedDashboardPath =
          me?.linked === true
            ? DASHBOARD_PATHS[
                me?.is_platform_admin ? "PLATFORM_ADMIN" : me?.primary_actor
              ] || ""
            : "";

        if (!cancelled) {
          setDashboardPath(resolvedDashboardPath);
        }
      } catch (error) {
        console.error("Could not resolve dashboard destination:", error);

        if (!cancelled) {
          setDashboardPath("");
        }
      }
    };

    loadCurrentUser();

    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    isAuthenticated,
    user?.email_verified,
    getAccessTokenSilently,
  ]);

  const defaultItems = [
    {
      key: "services",

      label: t("menuServices"),

      to: "/services",

      icon: Landmark,
    },

    // {

    //   key: "aboutTheProject",

    //   label: t("menuAbout"),

    //   to: "/",

    //   icon: Info,

    // },

    {
      key: "contact",

      label: t("menuContact"),

      to: "/contact",

      icon: MessagesSquare,
    },

    // {

    //   key: "guides",

    //   label: t("menuGuides"),

    //   to: "/",

    //   icon: Book,

    // },

    {
      key: "faq",

      label: t("menuFaq"),

      to: "/faq",

      icon: FileQuestionMark,
    },
  ];

  const baseMenuItems = items?.length ? items : defaultItems;

  const menuItems = dashboardPath
    ? [
        {
          key: "myDashboard",
          label: t("menuMyDashboard", "My Dashboard"),
          to: dashboardPath,
          icon: LayoutDashboard,
        },
        ...baseMenuItems,
      ]
    : baseMenuItems;

  const isMobileOpen =
    mobileOpen !== undefined ? mobileOpen : internalMobileOpen;

  const openMobileMenu = useCallback(() => {
    onOpen?.();

    if (mobileOpen === undefined) {
      setInternalMobileOpen(true);
    }
  }, [mobileOpen, onOpen]);

  const closeMobileMenu = useCallback(() => {
    onClose?.();

    if (mobileOpen === undefined) {
      setInternalMobileOpen(false);
    }
  }, [mobileOpen, onClose]);

  const handleTriggerClick = () => {
    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 760px)").matches;

    if (isMobile) {
      openMobileMenu();

      return;
    }

    setPinned((previous) => !previous);
  };

  useEffect(() => {
    if (!pinned) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setPinned(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [pinned]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      setPinned(false);

      closeMobileMenu();
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [closeMobileMenu]);

  useEffect(() => {
    if (!isMobileOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileOpen]);

  const mobileDrawer =
    typeof document !== "undefined"
      ? createPortal(
          <AnimatePresence>
            {isMobileOpen && (
              <div className="landing-menu__mobile-root">
                <motion.button
                  type="button"
                  className="landing-menu__mobile-overlay"
                  onClick={closeMobileMenu}
                  aria-label={t("closeMenu", "Close menu")}
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  transition={{
                    duration: prefersReducedMotion ? 0.01 : 0.22,
                  }}
                />

                <motion.aside
                  className="landing-menu__mobile"
                  initial={{
                    x: prefersReducedMotion ? 0 : "-102%",
                  }}
                  animate={{
                    x: 0,
                  }}
                  exit={{
                    x: prefersReducedMotion ? 0 : "-102%",
                  }}
                  transition={{
                    duration: prefersReducedMotion ? 0.01 : 0.36,

                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <div className="landing-menu__mobile-header">
                    <span className="landing-menu__mobile-title">
                      {menuTitle}
                    </span>

                    <button
                      type="button"
                      className="landing-menu__mobile-close"
                      onClick={closeMobileMenu}
                      aria-label={t("closeMenu", "Close menu")}
                    >
                      <X size={19} />
                    </button>
                  </div>

                  <nav
                    className="landing-menu__mobile-nav"
                    aria-label={t("menuTitle", "Landing page navigation")}
                  >
                    {menuItems.map((item, index) => {
                      const Icon = item.icon;

                      return (
                        <motion.div
                          key={item.key}
                          initial={{
                            opacity: 0,

                            x: prefersReducedMotion ? 0 : -10,
                          }}
                          animate={{
                            opacity: 1,

                            x: 0,
                          }}
                          transition={{
                            delay: prefersReducedMotion
                              ? 0
                              : 0.05 + index * 0.045,

                            duration: prefersReducedMotion ? 0.01 : 0.28,

                            ease: [0.22, 1, 0.36, 1],
                          }}
                        >
                          <NavLink
                            to={item.to}
                            onClick={closeMobileMenu}
                            className={({ isActive }) =>
                              `landing-menu__mobile-item ${
                                isActive && !isHomePage ? "is-active" : ""
                              }`
                            }
                          >
                            <Icon className="landing-menu__mobile-icon" />

                            <span>{item.label}</span>
                          </NavLink>
                        </motion.div>
                      );
                    })}
                  </nav>
                </motion.aside>
              </div>
            )}
          </AnimatePresence>,

          document.body,
        )
      : null;

  return (
    <>
      <aside
        ref={menuRef}
        className={`landing-menu ${pinned ? "is-pinned" : ""}`}
      >
        <motion.button
          type="button"
          className="landing-menu__button"
          onClick={handleTriggerClick}
          whileHover={
            prefersReducedMotion
              ? undefined
              : {
                  y: -1,
                }
          }
          whileTap={
            prefersReducedMotion
              ? undefined
              : {
                  scale: 0.98,
                }
          }
          aria-expanded={pinned || isMobileOpen}
          aria-haspopup="menu"
        >
          <span className="landing-menu__title">
            <Menu className="landing-menu__icon" />

            {menuTitle}
          </span>
        </motion.button>

        <nav
          className="landing-menu__nav"
          aria-label={t("menuTitle", "Landing page navigation")}
        >
          {menuItems.map((item) => {
            const Icon = item.icon;

            return (
              <NavLink
                key={item.key}
                to={item.to}
                onClick={() => setPinned(false)}
                className={({ isActive }) =>
                  `landing-menu__item ${
                    isActive && !isHomePage ? "is-active" : ""
                  }`
                }
              >
                <span className="landing-menu__item-motion">
                  <Icon className="landing-menu__nav-icon" />

                  <span className="landing-menu__label">{item.label}</span>
                </span>
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {mobileDrawer}
    </>
  );
}
